import { InputWithCharacterLimitGroup } from '@/components/admin/input-groups/input-with-character-limit-group/InputWithCharacterLimitGroup';
import { SingleSelectInputGroup } from '@/components/admin/input-groups/single-select-input-group/SingleSelectInputGroup';
import { TextAreaWithCharacterLimitGroup } from '@/components/admin/input-groups/text-area-with-character-limit-group/TextAreaWithCharacterLimitGroup';
import { TEAM_CATEGORY_TEXT, TEAM_CATEGORY_VALIDATION } from '@/const/admin/team';
import { useFormManager } from '@/hooks/admin/use-form-manager/useFormManager';
import { VisibilityStatus } from '@/types/admin/common';
import { TeamCategory } from '@/types/admin/team-category';
import { TEAM_CATEGORY_VALIDATION_FUNCTIONS } from '@/validation/admin/team-category-schema/team-category-schema';
import { forwardRef, useEffect, useState } from 'react';

export interface TranslateTeamCategoryFormValues {
    name: string;
    description: string;
}

export interface TranslateTeamCategoryFormErrorState {
    name: string | undefined;
    description: string | undefined | string[];
    [key: string]: string | string[] | undefined;
}

export interface TranslateTeamCategoryFormRef {
    submit: (status?: VisibilityStatus) => Promise<void>;
    isValid: () => boolean;
    isDirty: () => boolean;
}

export interface TranslateTeamCategoryFormProps {
    onSubmit: (data: TranslateTeamCategoryFormValues, status?: VisibilityStatus) => void | Promise<void>;
    categories: TeamCategory[];
    initialData?: TranslateTeamCategoryFormValues | null;
    formDisabled?: boolean;
    onCategoryChange?: (category: TeamCategory | null) => void;
    onValidationChange?: (isValid: boolean) => void;
    onDirtyChange?: (isDirty: boolean) => void;
    selectedCategory?: TeamCategory | null;
}

const DEFAULT_FORM_STATE: TranslateTeamCategoryFormValues = {
    name: '',
    description: '',
};

const validateForm = (
    formState: TranslateTeamCategoryFormValues,
    _isPublishing: boolean,
): TranslateTeamCategoryFormErrorState => {
    return {
        name: TEAM_CATEGORY_VALIDATION_FUNCTIONS.validateName(formState.name),
        description: TEAM_CATEGORY_VALIDATION_FUNCTIONS.validateDescription(formState.description),
    };
};
export const TranslateTeamCategoryForm = forwardRef<TranslateTeamCategoryFormRef, TranslateTeamCategoryFormProps>(
    (
        {
            initialData = null,
            onSubmit,
            categories,
            formDisabled,
            onCategoryChange,
            onValidationChange,
            onDirtyChange,
            selectedCategory,
        }: TranslateTeamCategoryFormProps,
        ref,
    ) => {
        const { formState, setFormState, errors, setErrors, isSubmitting } = useFormManager<
            TranslateTeamCategoryFormValues,
            TranslateTeamCategoryFormErrorState
        >({
            defaultFormState: DEFAULT_FORM_STATE,
            initialData,
            validateForm,
            onValidationChange,
            ref,
            onSubmit: (data, _status) => onSubmit(data),
        });
        const [localSelectedCategory, setLocalSelectedCategory] = useState<TeamCategory | null>(null);
        const activeCategory = selectedCategory !== undefined ? selectedCategory : localSelectedCategory;

        useEffect(() => {
            const isDirty = JSON.stringify(formState) !== JSON.stringify(initialData);
            onDirtyChange?.(isDirty);
        }, [formState, initialData, onDirtyChange]);

        const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setFormState((prev) => ({ ...prev, name: e.target.value }));
        };
        const handleCategoryChange = (category: TeamCategory | null) => {
            if (selectedCategory === undefined) {
                setLocalSelectedCategory(category);
            }
            onCategoryChange?.(category);
        };

        const handleNameBlur = () => {
            const normalized = formState.name.trim().replace(/\s+/g, ' ');
            if (normalized !== formState.name) {
                setFormState((prev) => ({ ...prev, name: normalized }));
            }
            const error = TEAM_CATEGORY_VALIDATION_FUNCTIONS.validateName(normalized);
            setErrors((prev) => ({ ...prev, name: error }));
        };

        const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setFormState((prev) => ({ ...prev, description: e.target.value }));
        };

        const handleDescriptionBlur = () => {
            const normalized = formState.description.trim().replace(/\s+/g, ' ');
            if (normalized !== formState.description) {
                setFormState((prev) => ({ ...prev, description: normalized }));
            }
            const error = TEAM_CATEGORY_VALIDATION_FUNCTIONS.validateDescription(normalized);
            setErrors((prev) => ({ ...prev, description: error }));
        };

        return (
            <form
                onSubmit={(e) => e.preventDefault()}
                className="team-category-modal-form"
                id={'translate-category-form'}
                noValidate
            >
                <SingleSelectInputGroup
                    label={TEAM_CATEGORY_TEXT.FORM.LABEL.CATEGORY}
                    isRequired
                    options={categories}
                    getOptionId={(c) => c.id}
                    getOptionName={(c) => c.name}
                    disabled={isSubmitting || formDisabled}
                    onChange={handleCategoryChange}
                    value={activeCategory || undefined}
                    placeholder="Оберіть категорію"
                    id={'category-select'}
                />

                <InputWithCharacterLimitGroup
                    label={TEAM_CATEGORY_TEXT.FORM.LABEL.NAME}
                    error={errors.name}
                    isRequired
                    value={formState.name}
                    onChange={handleNameChange}
                    onBlur={handleNameBlur}
                    disabled={isSubmitting || !activeCategory}
                    maxLength={TEAM_CATEGORY_VALIDATION.name.max}
                    name={'name'}
                    id={'name'}
                    showCounterBelow={true}
                />

                <TextAreaWithCharacterLimitGroup
                    label={TEAM_CATEGORY_TEXT.FORM.LABEL.DESCRIPTION}
                    error={typeof errors.description === 'string' ? errors.description : undefined}
                    value={formState.description}
                    onChange={handleDescriptionChange}
                    onBlur={handleDescriptionBlur}
                    isRequired
                    disabled={isSubmitting || !activeCategory}
                    rows={5}
                    maxLength={TEAM_CATEGORY_VALIDATION.description.max}
                    name={'description'}
                    id={'description'}
                />
            </form>
        );
    },
);
